import type { CSSProperties, JSX } from 'react';
import type { Palette } from '../../../_theme/palette.js';
import { MediaBlock } from './MediaBlock.js';
import { usePipe } from './PipingProvider.js';
import type { SurveyPage } from '../types.js';

interface PageHeaderProps {
  palette: Palette;
  page: SurveyPage;
}

export function PageHeader({ palette, page }: PageHeaderProps): JSX.Element {
  const pipe = usePipe();
  const media = page.media?.[0];
  const layout = page.mediaLayout ?? (media ? 'image-top' : 'text-only');
  const hasMedia = Boolean(media?.url) && layout !== 'text-only' && layout !== 'background';

  return (
    <header style={wrapperStyle(layout)}>
      {hasMedia && media && <MediaBlock media={media} radius={12} maxHeight={200} />}
      <div style={textColumnStyle(layout)}>
        <h2 style={titleStyle(palette)}>{pipe(page.title)}</h2>
        {page.description && <p style={descriptionStyle(palette)}>{pipe(page.description)}</p>}
      </div>
    </header>
  );
}

function wrapperStyle(layout: SurveyPage['mediaLayout']): CSSProperties {
  const base: CSSProperties = { marginBottom: 20, display: 'flex', gap: 16 };
  if (layout === 'image-left') return { ...base, flexDirection: 'row', alignItems: 'flex-start' };
  if (layout === 'image-right') return { ...base, flexDirection: 'row-reverse', alignItems: 'flex-start' };
  return { ...base, flexDirection: 'column' };
}

function textColumnStyle(layout: SurveyPage['mediaLayout']): CSSProperties {
  if (layout === 'image-left' || layout === 'image-right') return { flex: 1 };
  return {};
}

function titleStyle(palette: Palette): CSSProperties {
  return { margin: '0 0 6px', color: palette.text, fontSize: 24, fontWeight: 700 };
}

function descriptionStyle(palette: Palette): CSSProperties {
  return { color: palette.textMuted, margin: 0, fontSize: 14, lineHeight: 1.5 };
}
